import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import GroupRemoveIcon from '@mui/icons-material/GroupRemove';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import {
  alpha,
  Box,
  Divider,
  IconButton,
  lighten,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { startAdhocProfileAnalysis } from 'API/linkedin-funnel';
import { ErrorMessages, ProductGroups } from 'enums';
import { ContactFilter, Qualifications } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { ContactsSearchStatuses, FunnelContact, Search } from 'features/co-pilot/co-pilot.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotatingLines } from 'react-loader-spinner';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import { isViableForHeaderSwitch } from '../contact-activation';
import PaiRoundTextField from '../../../../../ui/base-components/pai-round-text-field';
import AddContactMenu from './add-contact-menu';
import AnalysisAlreadyInProgressDialog from './analysis-already-in-progress-dialog';
import ContactItem from './contact-item';
import useColumnOrder from './use-column-order';

interface Props {
  funnelId: string;
  contacts: FunnelContact[];
  searches: Search[];
  productGroup: ProductGroups;
  hasJudgingSettings: boolean;

  contactsPage: number;
  contactsSearchParam: string;
  contactsPerPage: number;
  totalContacts: number;

  contactsLoading: boolean;
  aiJudgementProcessing: boolean;
  profileAnalysisProcessing: boolean;

  contactFilter: ContactFilter;
  activeQualifications: Qualifications[];

  abortRequested: boolean;
  abortLoading: boolean;

  deleteMultipleContacts: boolean;
  contactsToDelete: FunnelContact[];

  handleSingleContactIsActiveChange(contact: FunnelContact): () => void;

  handleMultiContactIsActiveChange(): void;

  handleChangeContactsPage(event: any, value: number): void;

  handleContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearContactsSearchField(): void;

  handleOpenContactDrawer(linkedInURL: string | undefined, profileId: string | undefined, campaignId: string | undefined): () => void;

  handleOpenAddSingleContactDialog(): void;

  handleOpenDeleteContactPermanentlyDialog(contact: FunnelContact): () => void;

  handleAddMultipleContacts(): void;

  handleOpenChangeAiJudgementDialog(contact: FunnelContact): () => void;

  handleRemoveContactFromBlacklist(linkedInURL: string): () => void;

  handleRemoveContactFromSuspended(linkedInURL: string): () => void;

  handleOpenUpdateSexDialog(contact: FunnelContact): () => void;

  handleRecoverContactFromDeleted(linkedInURL: string): () => void;

  handleContactFilterChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleActiveQualificationsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAbortProcessing(): void;

  toggleDeleteMultipleContacts(): void;

  handleContactsToDeleteChange(contact: FunnelContact): (event: React.MouseEvent<HTMLInputElement>) => void;

  handleMultipleContactsToDeleteChange(event: React.MouseEvent<HTMLInputElement>): void;

  handleOpenDeleteMultipleContactsPermanentlyDialog(): void;
}

const ContactsTable: React.FC<Props> = ({
  funnelId,
  contacts,
  searches,
  productGroup,
  hasJudgingSettings,
  contactsPage,
  contactsSearchParam,
  contactsPerPage,
  totalContacts,
  contactsLoading,
  aiJudgementProcessing,
  profileAnalysisProcessing,
  contactFilter,
  activeQualifications,
  abortRequested,
  abortLoading,
  deleteMultipleContacts,
  contactsToDelete,
  handleSingleContactIsActiveChange,
  handleMultiContactIsActiveChange,
  handleChangeContactsPage,
  handleContactsSearchChange,
  handleClearContactsSearchField,
  handleOpenContactDrawer,
  handleOpenAddSingleContactDialog,
  handleAddMultipleContacts,
  handleOpenDeleteContactPermanentlyDialog,
  handleOpenChangeAiJudgementDialog,
  handleRemoveContactFromBlacklist,
  handleRemoveContactFromSuspended,
  handleOpenUpdateSexDialog,
  handleRecoverContactFromDeleted,
  handleContactFilterChange,
  handleActiveQualificationsChange,
  handleAbortProcessing,
  toggleDeleteMultipleContacts,
  handleMultipleContactsToDeleteChange,
  handleContactsToDeleteChange,
  handleOpenDeleteMultipleContactsPermanentlyDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [adhocLoading, setAdhocLoading] = useState(false);
  const [analysisAlreadyInProgressDialog, setAnalysisAlreadyInProgressDialog] = useState<{ open: boolean }>({ open: false });
  const sensors = useSensors(useSensor(KeyboardSensor), useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const isSearchRunning = searches.some((search) => search.status === ContactsSearchStatuses.RUNNING);
  const isSynchronizingLinkedinAccount = searches.some((search) => search.status === ContactsSearchStatuses.SYNCHRONIZING_LINKEDIN_ACCOUNT);
  const isProcessing = aiJudgementProcessing || profileAnalysisProcessing;

  const getVisibleColumns = () => {
    const columns = {
      aiJudgement: false,
      reason: false,
      email: false,
      searchName: false,
      customField: false,
    };

    contacts.forEach((contact) => {
      if (contact.aiJudgement) {
        columns.aiJudgement = true;
      }
      if (contact.aiJudgementExplanation) {
        columns.reason = true;
      }
      if (contact.email) {
        columns.email = true;
      }
      if (contact.searchName || contact.fileName) {
        columns.searchName = true;
      }
      if (contact.customField) {
        columns.customField = true;
      }
    });

    return columns;
  };

  /**
   * Who the "activate all" switch actually speaks for.
   *
   * `bulkUpdateContacts` runs every activation through
   * `enforceContactedElsewhereBeforeActivation` and drops the ones already approached from
   * another campaign — correctly, but silently. Leaving them in this list made the header
   * switch claim a state the server refused to produce: flip it on, two grade-A candidates
   * stay off, no reason anywhere. That is 7b1c596f, reported a second time as 40db0ad9.
   *
   * Sex and profile analysis used to sit on this list too. Fresh search rows
   * never have either, so the switch disabled itself on every new list and
   * the click went nowhere.
   */
  const viableContacts = contacts.filter(isViableForHeaderSwitch);
  const headerSwitchOn = !_.isEmpty(viableContacts) && _.every(viableContacts, (contact) => !!contact.isActive);
  const headerSwitchDisabled = _.isEmpty(viableContacts) || contactsLoading || aiJudgementProcessing || profileAnalysisProcessing;

  const visibleColumns = getVisibleColumns();

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey) || t(key);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey) || t(key);
  };

  // Define columns with drag and drop support
  const defaultColumnOrder = [
    'isActive',
    'name',
    'aiJudgement',
    'company',
    'jobTitle',
    'location',
    'email',
    'searchName',
    'customField',
    'linkedInURL',
  ];

  const [columnOrder, updateColumnOrder] = useColumnOrder('contacts-table-column-order', defaultColumnOrder);

  // Filter visible columns based on current order
  const visibleColumnOrder = columnOrder.filter((colId: string) => {
    if (colId === 'aiJudgement') return visibleColumns.aiJudgement;
    if (colId === 'email') return visibleColumns.email;
    if (colId === 'searchName') return visibleColumns.searchName;
    if (colId === 'customField') return visibleColumns.customField;
    return true; // Always show other columns
  });

  const handleDragEnd = (result: DragEndEvent) => {
    const { active, over } = result;

    if (active.id === over?.id) {
      return;
    }

    const oldIndex = columnOrder.indexOf(active.id as string);
    const newIndex = columnOrder.indexOf(over?.id as string);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
    updateColumnOrder(newOrder);
  };

  const contactFilterOptions = [
    { label: getLocalizedText('allContacts'), value: ContactFilter.ALL },
    { label: getLocalizedText('isActiveContacts'), value: ContactFilter.IS_ACTIVE },
    { label: getLocalizedText('isNotActiveContacts'), value: ContactFilter.IS_NOT_ACTIVE },
    { label: getLocalizedText('isFinishedContacts'), value: ContactFilter.IS_FINISHED },
    { label: getLocalizedText('contactedContacts'), value: ContactFilter.CONTACTED },
    { label: getLocalizedText('notContactedContacts'), value: ContactFilter.NOT_CONTACTED },
    { label: getLocalizedText('contactsInOtherCampaigns'), value: ContactFilter.ALREADY_CONTACTED },
    { label: getLocalizedText('contactedElsewhere'), value: ContactFilter.CONTACTED_ELSEWHERE },
    { label: getLocalizedText('uncertainSexContacts'), value: ContactFilter.UNCERTAIN_SEX },
    { label: getLocalizedText('blacklistedContacts'), value: ContactFilter.BLACKLISTED },
    { label: getLocalizedText('suspendedContacts'), value: ContactFilter.SUSPENDED },
    { label: getLocalizedText('deletedContacts'), value: ContactFilter.DELETED },
  ];

  const qualificationFilterOptions = [
    { label: getLocalizedText('topCandidates'), value: Qualifications.A },
    { label: getLocalizedText('strongContenders'), value: Qualifications.B },
    { label: getLocalizedText('backupCandidatePool'), value: Qualifications.C },
    { label: t('notJudged'), value: Qualifications.NOT_JUDGED },
  ];

  // Sortable table header component
  const SortableTableHeader = ({ columnId, columnLabel }: { columnId: string; columnLabel: string }) => {
    const { classes } = useStyles();

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: columnId });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <TableCell ref={setNodeRef} style={style} className={classes.sortableHeader}>
        <Stack direction="row" alignItems="center" gap={1}>
          <div {...attributes} {...listeners} className={classes.dragHandle}>
            <DragHandleIcon className={classes.dragHandleIcon} />
          </div>
          <Typography className={classes.tableHeaderText}>{columnLabel}</Typography>
        </Stack>
      </TableCell>
    );
  };

  // Add state for menu anchor
  const [addMenuAnchorEl, setAddMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isAddMenuOpen = Boolean(addMenuAnchorEl);

  const handleAddMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchorEl(null);
  };

  const searchTerms = [t('firstName'), t('lastName'), t('email'), t('jobTitle'), t('company'), t('location'), t('searchName')];

  const handleStartAdhocProfileAnalysis = async () => {
    if (adhocLoading) return;
    setAdhocLoading(true);

    const res = await startAdhocProfileAnalysis(funnelId);
    if (res?.error) {
      if (res.error.message?.includes(ErrorMessages.PROFILE_ANALYSIS_IN_PROGRESS_PARTIAL)) {
        handleOpenAnalysisAlreadyInProgressDialog();
      } else {
        handleOpenErrorDialog(res.error, '/campaigns/:campaignId/adhoc/analyze', 'post');
      }
    } else {
      // Kick off immediate refresh so the processing flags appear
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
    setAdhocLoading(false);
  };

  /**
   * Handle analysis already in progress dialog
   */

  const handleOpenAnalysisAlreadyInProgressDialog = () => {
    setAnalysisAlreadyInProgressDialog({ open: true });
  };

  const handleCloseAnalysisAlreadyInProgressDialog = () => {
    setAnalysisAlreadyInProgressDialog({ open: false });
  };

  return (
    <div className={classes.contactList}>
      <div className={classes.listHeader}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <PaiRoundTextField
              style={{ width: '300px' }}
              value={contactsSearchParam}
              variant={'search'}
              placeholder={t('enterSearchTermHere')}
              onClear={handleClearContactsSearchField}
              onChange={handleContactsSearchChange}
            />
            <PaiHelpIcon
              tooltipText={t('searchTermsAre', {
                searchTerms: searchTerms.map((term) => `• ${term}`).join('\n'),
              })}
            />
            <Divider orientation="vertical" flexItem />
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <Typography className={classes.greyText}>{t('filter')}:</Typography>
              <PaiSelect value={contactFilter} variant={'background'} dense={true} style={{ width: '200px' }} onChange={handleContactFilterChange}>
                {contactFilterOptions.map((option) => (
                  <PaiMenuItem key={option.value} value={option.value}>
                    {option.label}
                  </PaiMenuItem>
                ))}
              </PaiSelect>
            </Stack>
            {hasJudgingSettings ? (
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Typography className={classes.greyText}>{t('assessment')}:</Typography>
                <PaiSelect
                  value={activeQualifications}
                  variant={'background'}
                  multiple={true}
                  dense={true}
                  style={{ width: '200px' }}
                  onChange={handleActiveQualificationsChange}
                >
                  {qualificationFilterOptions.map((option) => (
                    <PaiMenuItem key={option.value} value={option.value} isActive={activeQualifications.includes(option.value)}>
                      {option.label}
                    </PaiMenuItem>
                  ))}
                </PaiSelect>
              </Stack>
            ) : null}
          </Stack>
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            {totalContacts > 0 ? (
              <>
                <PaiTooltip title={getLocalizedText('deleteMultipleContacts')}>
                  <IconButton
                    className={cx(classes.iconButton, deleteMultipleContacts && classes.activeIconButton)}
                    size={'small'}
                    color={'error'}
                    onClick={toggleDeleteMultipleContacts}
                  >
                    <GroupRemoveIcon />
                  </IconButton>
                </PaiTooltip>
                <PaiTooltip title={t('analyze')}>
                  <span>
                    <IconButton
                      className={classes.iconButton}
                      size={'small'}
                      color={'primary'}
                      onClick={handleStartAdhocProfileAnalysis}
                      disabled={contactsLoading || isSearchRunning || isProcessing || adhocLoading}
                    >
                      <RocketLaunchIcon />
                    </IconButton>
                  </span>
                </PaiTooltip>
                <Divider orientation="vertical" flexItem />
              </>
            ) : null}
            <PaiButton text={t('add')} size={'small'} border={'rounded'} startIcon={<AddIcon />} action={handleAddMenuClick} />
          </Stack>
        </Stack>
      </div>
      <div className={classes.listContent}>
        {isSearchRunning || isSynchronizingLinkedinAccount ? (
          <Stack className={classes.loader} alignItems={'center'} justifyContent={'center'} gap={3}>
            <RotatingLines strokeWidth={'3'} width={'100'} strokeColor={theme.palette.primary.main} />
            <div>
              <Typography className={classes.loadingTextTitle} align={'center'} mb={'8px'}>
                {isSynchronizingLinkedinAccount ? t('linkedinAccountLoginInProgress') : getLocalizedText('contactLoadingInProgress')}
              </Typography>
              <Typography className={classes.loadingText} align={'center'}>
                {getLocalizedText('contactLoadingMayTakeAMoment')}
              </Typography>
            </div>
          </Stack>
        ) : null}
        <TableContainer sx={{ height: '100%', width: '100%' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table className={classes.table}>
              <TableHead>
                <SortableContext items={visibleColumnOrder} strategy={horizontalListSortingStrategy}>
                  <TableRow className={classes.tableHeadRow}>
                    {/* Fixed checkbox column */}
                    <TableCell sx={{ minWidth: '100px' }}>
                      {!deleteMultipleContacts ? (
                        <PaiTooltip
                          title={
                            _.isEmpty(viableContacts)
                              ? t('activateAllContactsDisabled')
                              : headerSwitchOn
                                ? t('unselectAll')
                                : t('selectAll')
                          }
                        >
                          <span>
                            <PaiSwitch
                              checked={headerSwitchOn}
                              disabled={headerSwitchDisabled}
                              onClick={handleMultiContactIsActiveChange}
                            />
                          </span>
                        </PaiTooltip>
                      ) : (
                        <Box sx={{ width: '89px !important' }}>
                          <PaiCheckbox
                            checked={contactsToDelete.length === contacts.length}
                            indeterminate={contactsToDelete.length > 0 && contactsToDelete.length < contacts.length}
                            onClick={handleMultipleContactsToDeleteChange}
                          />
                        </Box>
                      )}
                    </TableCell>

                    {/* Draggable columns */}
                    {visibleColumnOrder.map((columnId) => {
                      let columnLabel = '';

                      switch (columnId) {
                        case 'name':
                          columnLabel = t('name');
                          break;
                        case 'aiJudgement':
                          columnLabel = t('qualifiedAs');
                          break;
                        case 'company':
                          columnLabel = t('company');
                          break;
                        case 'jobTitle':
                          columnLabel = t('jobTitle');
                          break;
                        case 'location':
                          columnLabel = t('location');
                          break;
                        case 'email':
                          columnLabel = t('email');
                          break;
                        case 'searchName':
                          columnLabel = t('source');
                          break;
                        case 'customField':
                          columnLabel = t('customField');
                          break;
                        case 'linkedInURL':
                          columnLabel = t('linkedInURL');
                          break;
                        default:
                          return null;
                      }

                      return <SortableTableHeader key={columnId} columnId={columnId} columnLabel={columnLabel} />;
                    })}
                  </TableRow>
                </SortableContext>
              </TableHead>
              <TableBody>
                {contacts.map((contact, index) => (
                  <ContactItem
                    key={index}
                    funnelId={funnelId}
                    contact={contact}
                    disabled={
                      // Block editing while the contact itself is still being processed.
                      // If profile analysis is running, allow edits for contacts that have already been analysed (identified by "preprocessedProfileAnalysis").
                      // Likewise, if an AI-judgement run is in progress, allow edits for contacts that are already judged.
                      (profileAnalysisProcessing && !contact.preprocessedProfileAnalysis) || (aiJudgementProcessing && !contact.isJudged)
                    }
                    contactsLoading={contactsLoading}
                    contactsSearchParam={contactsSearchParam}
                    columnOrder={visibleColumnOrder}
                    deleteMultipleContacts={deleteMultipleContacts}
                    isSelectedForDeletion={contactsToDelete.includes(contact)}
                    getVisibleColumns={getVisibleColumns}
                    getLocalizedText={getLocalizedText}
                    handleSingleContactIsActiveChange={handleSingleContactIsActiveChange}
                    handleOpenContactDrawer={handleOpenContactDrawer}
                    handleOpenDeleteContactPermanentlyDialog={handleOpenDeleteContactPermanentlyDialog}
                    handleOpenChangeAiJudgementDialog={handleOpenChangeAiJudgementDialog}
                    handleRemoveContactFromBlacklist={handleRemoveContactFromBlacklist}
                    handleRemoveContactFromSuspended={handleRemoveContactFromSuspended}
                    handleOpenUpdateSexDialog={handleOpenUpdateSexDialog}
                    handleRecoverContactFromDeleted={handleRecoverContactFromDeleted}
                    handleContactsToDeleteChange={handleContactsToDeleteChange}
                  />
                ))}
              </TableBody>
            </Table>
          </DndContext>
        </TableContainer>
      </div>
      <Stack className={classes.listFooter} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Typography className={classes.contactCountText}>{`# ${totalContacts} ${getLocalizedText('contacts')}`}</Typography>
          {abortRequested || abortLoading ? (
            <Stack direction={'row'} alignItems={'center'} gap={3} ml={'16px'}>
              <PaiDotLoader size={'small'} inline={true} />
              <Typography className={classes.infoText}>{t('stopProcessing')}</Typography>
            </Stack>
          ) : aiJudgementProcessing ? (
            <Stack direction={'row'} alignItems={'center'} gap={3} ml={'16px'}>
              <PaiDotLoader size={'small'} inline={true} />
              <Typography className={classes.infoText}>{t('qualificationInProgress')}</Typography>
            </Stack>
          ) : profileAnalysisProcessing ? (
            <Stack direction={'row'} alignItems={'center'} gap={3} ml={'16px'}>
              <PaiDotLoader size={'small'} inline={true} />
              <Typography className={classes.infoText}>{t('profileAnalysisInProgress')}</Typography>
            </Stack>
          ) : null}

          {!deleteMultipleContacts ? (
            isProcessing && !abortRequested ? (
              <PaiButton
                text={t('stop')}
                size={'small'}
                border={'rounded'}
                color={'error'}
                startIcon={<CloseIcon />}
                action={handleAbortProcessing}
                disabled={abortLoading}
              />
            ) : null
          ) : (
            <>
              <Typography className={classes.errorText}>{`${getLocalizedText('deleteMultipleContacts')}: ${contactsToDelete.length}`}</Typography>
              <PaiButton
                text={t('delete')}
                size={'small'}
                border={'rounded'}
                color={'error'}
                startIcon={<DeleteIcon />}
                action={handleOpenDeleteMultipleContactsPermanentlyDialog}
                disabled={contactsToDelete.length === 0}
              />
            </>
          )}
        </Stack>
        <PaiPagination
          total={totalContacts}
          itemsPerPage={contactsPerPage}
          page={contactsPage}
          handleChangePage={handleChangeContactsPage}
          disabled={isSearchRunning}
        />
      </Stack>

      <AddContactMenu
        anchorEl={addMenuAnchorEl}
        menuOpen={isAddMenuOpen}
        handleCloseMenu={handleAddMenuClose}
        handleAddSingleContact={handleOpenAddSingleContactDialog}
        handleAddMultipleContacts={handleAddMultipleContacts}
      />
      <AnalysisAlreadyInProgressDialog open={analysisAlreadyInProgressDialog.open} handleClose={handleCloseAnalysisAlreadyInProgressDialog} />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactList: {
    height: '100%',
    width: '100%',
    borderRadius: '12px',
    background: '#FFF',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  listHeader: {
    padding: '8px 16px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  headerIcon: {
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
  },
  headerText: {
    fontSize: theme.typography.pxToRem(16),
    '&.primary': {
      color: theme.palette.primary.main,
    },
    '&.secondary': {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.text.secondary,
    },
  },
  infoText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: alpha('#FFF', 0.9),
    zIndex: 99,
  },
  loadingTextTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  loadingText: {
    fontSize: theme.typography.pxToRem(16),
    maxWidth: '450px',
  },
  listContent: {
    position: 'relative',
    height: '100%',
    padding: '8px',
    overflow: 'auto',
  },
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9,
  },
  dragHandle: {
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: alpha('#000', 0.05),
    },
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragHandleIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.secondary,
    transition: 'color 0.2s ease-in-out',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  tableHeaderText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  sortableHeader: {
    minWidth: '350px',
    width: '350px',
    maxWidth: '350px',
  },
  table: {
    '& th': {
      border: 'none',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      '&:first-of-type': {
        borderTopLeftRadius: '6px',
        borderBottomLeftRadius: '6px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '6px',
        borderBottomRightRadius: '6px',
      },
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
      '& mark': {
        backgroundColor: lighten(theme.palette.secondary.main, 0.8),
      },
    },
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  errorText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.error.main,
  },
  contactCountText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  listFooter: {
    padding: '8px 16px',
    borderTop: `1px solid ${theme.palette.greys.light}`,
    marginTop: 'auto',
  },
  iconButton: {
    padding: '4px',
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '& .MuiSvgIcon': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  activeIconButton: {
    '&.MuiIconButton-colorError': {
      border: `1px solid ${theme.palette.error.main}`,
      backgroundColor: theme.palette.error.main,
      '& .MuiSvgIcon-root': {
        color: '#FFF',
      },
    },
  },
}));

export default ContactsTable;
